import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Link, NavLink } from 'react-router-dom';
import { Pie, Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
} from 'chart.js';
import formatPrice from '../../utils/formatPrice';
import '../../css/adminDashboard.css';
import { getNewOrderCount } from '../../services/orderService';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title
);

const AdminSidebar = () => {
  const [newOrders, setNewOrders] = useState(0);
  useEffect(() => {
    getNewOrderCount().then(setNewOrders).catch(() => setNewOrders(0));
  }, []);
  const Icon = ({ name }) => {
    const paths = {
      dashboard: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
      products: <><path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5z"/><path d="m4 7.5 8 4.5 8-4.5M12 12v9"/></>,
      categories: <><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></>,
      orders: <><path d="M6 3h12l2 4v14H4V7z"/><path d="M4 7h16M9 11h6"/></>,
      users: <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></>,
      coupons: <><path d="M3 8a3 3 0 0 0 0 6v4h18v-4a3 3 0 0 0 0-6V4H3z"/><path d="M12 4v14M9 8h.01M15 14h.01"/></>,
      exclusive: <><path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z"/></>,
      hero: <><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.5"/><path d="m3 16 5-5 4 4 3-3 6 6"/></>
    };
    return <span className="admin-nav-icon"><svg viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg></span>;
  };
  const handleAdminNavigation = () => {
    window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 0);
  };
  return <aside className="admin-sidebar">
    <div className="admin-brand">
      <div className="admin-brand-mark">M</div>
      <div><strong>Metro</strong><span>Ecommerce Admin</span></div>
    </div>
    <div className="admin-nav-label">Workspace</div>
    <nav>
      <NavLink to="/admin" onClick={handleAdminNavigation}><Icon name="dashboard"/><span>Dashboard</span></NavLink>
      <NavLink to="/admin/products" onClick={handleAdminNavigation}><Icon name="products"/><span>Products</span></NavLink>
      <NavLink to="/admin/categories" onClick={handleAdminNavigation}><Icon name="categories"/><span>Categories</span></NavLink>
      <NavLink to="/admin/orders" onClick={handleAdminNavigation} className="admin-orders-link"><Icon name="orders"/><span>Orders</span>{newOrders > 0 && <span className="admin-order-badge">{newOrders > 99 ? '99+' : newOrders}</span>}</NavLink>
      <NavLink to="/admin/users" onClick={handleAdminNavigation}><Icon name="users"/><span>Customers</span></NavLink>
      <NavLink to="/admin/coupons" onClick={handleAdminNavigation}><Icon name="coupons"/><span>Coupons</span></NavLink>
      <NavLink to="/admin/exclusive-banners" onClick={handleAdminNavigation}><Icon name="exclusive"/><span>Exclusive Banners</span></NavLink>
      <NavLink to="/admin/hero-banners" onClick={handleAdminNavigation}><Icon name="hero"/><span>Hero Banners</span></NavLink>
    </nav>
    <div className="admin-sidebar-footer"><span className="admin-status-dot"></span>Store operations online</div>
  </aside>;
};

const AdminDashboardPage = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    recentOrders: 0,
    orderStatuses: [],
    revenueByMonth: [],
    topProducts: []
  });

  useEffect(() => {
    const load = async () => {
      const { data } = await api.get('/admin/stats');
      setStats(data);
    };
    load();
  }, []);

  // Ensure data is arrays to prevent runtime errors
  const orderStatuses = Array.isArray(stats.orderStatuses) ? stats.orderStatuses : [];
  const revenueByMonth = Array.isArray(stats.revenueByMonth) ? stats.revenueByMonth : [];
  const topProducts = Array.isArray(stats.topProducts) ? stats.topProducts : [];

  // Prepare chart data
  const orderStatusData = {
    labels: orderStatuses.map(s => s._id || 'Unknown'),
    datasets: [{
      data: orderStatuses.map(s => s.count),
      backgroundColor: [
        '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6'
      ],
      borderWidth: 1
    }]
  };

  const revenueData = {
    labels: revenueByMonth.map(m => `${m._id.month}/${m._id.year}`),
    datasets: [{
      label: 'Revenue',
      data: revenueByMonth.map(m => m.revenue),
      backgroundColor: '#3498db',
      borderColor: '#2980b9',
      borderWidth: 1
    }]
  };

  const topProductsData = {
    labels: topProducts.map(p => p.name),
    datasets: [{
      label: 'Units Sold',
      data: topProducts.map(p => p.totalSold),
      backgroundColor: '#e74c3c',
      borderColor: '#c0392b',
      borderWidth: 1
    }]
  }
  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-page-heading"><div><span>Overview</span><h1>Dashboard</h1><p>Monitor your store performance and recent activity.</p></div></div>

        {/* Stats Cards */}
        <div className="admin-grid">
          <div className="admin-card">
            <h3>Total Users</h3>
            <p>{stats.totalUsers}</p>
          </div>
          <div className="admin-card">
            <h3>Total Orders</h3>
            <p>{stats.totalOrders}</p>
            <div className="change">+{stats.recentOrders} this month</div>
          </div>
          <div className="admin-card">
            <h3>Total Revenue</h3>
            <p>{formatPrice(stats.totalRevenue)}</p>
          </div>
          <div className="admin-card">
            <h3>Active Customers</h3>
            <p>{Math.floor(stats.totalUsers * 0.7)}</p>
            <div className="change">70% active rate</div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-container">
            <h3>Order Status Distribution</h3>
            <center><Pie data={orderStatusData} className="piechart" /></center>
          </div>
          <div className="chart-container">
            <h3>Revenue Trend (Last 6 Months)</h3>
            <Bar data={revenueData} />
          </div>
        </div>

        {/* Top Products */}
        <div className="charts-section">
          <div className="chart-container">
            <h3>Top Selling Products</h3>
            <Bar data={topProductsData} />
          </div>
          <div className="activity-section">
            <h3>Recent Activity</h3>
            {topProducts.map((product, index) => (
              <div key={index} className="activity-item">
                <div className="product-name">{product.name}</div>
                <div className="sales-data">
                  <div className="units">{product.totalSold} units</div>
                  <div className="revenue">{formatPrice(product.revenue)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
export { AdminSidebar };
